import React, { useEffect, useState } from "react";
import axios from "axios";
import "../css/vehicle.css";
import useAuthRedirect from "../hooks/useAuthRedirect";
import AdminNav from "../components/adminNav";
import Time from "../components/time";
import { useParams, Link, useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { hasPermission } from "../utils/permissionHelper";
import Access from "../components/access";

/* ================= helpers ================= */

const pad2 = (n) => String(n).padStart(2, "0");

const formatDMY = (dLike) => {
  const d = new Date(dLike);
  if (Number.isNaN(d.getTime())) return "";
  return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
};

const format12h = (d) => {
  if (!d) return "";
  const h = d.getHours();
  const m = d.getMinutes();
  const period = h < 12 ? "AM" : "PM";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${pad2(m)} ${period}`;
};

// decimal hours (e.g., 19.5) -> "HH:MM"
const decToHHMM = (decimalHrs) => {
  if (decimalHrs === null || decimalHrs === undefined || decimalHrs === "") return "";
  const n = Number(decimalHrs);
  if (Number.isNaN(n)) return String(decimalHrs);
  const hh = Math.floor(n);
  const mm = Math.round((n - hh) * 60);
  return `${pad2(hh)}:${pad2(mm)}`;
};

// decimal hours -> AM/PM (visual only; assumes value is time of day)
const decToAMPM = (decimalHrs) => {
  const hhmm = decToHHMM(decimalHrs);
  if (!hhmm.includes(":")) return "";
  const [hh, mm] = hhmm.split(":").map((x) => parseInt(x, 10));
  const t = new Date();
  t.setHours(hh, mm, 0, 0);
  return format12h(t);
};

// your "Working Hours" formatting rule
const formatWorkingTotal = (hoursDecimal) => {
  if (hoursDecimal === null || hoursDecimal === undefined || hoursDecimal === "") return "";
  const n = Number(hoursDecimal);
  if (Number.isNaN(n)) return String(hoursDecimal);

  if (n < 24) {
    const s = Number.isInteger(n) ? `${n}` : `${parseFloat(n.toFixed(1))}`;
    return `${s} hours`;
  }
  const days = Math.floor(n / 24);
  const rem = n - days * 24;
  if (Math.abs(rem) < 1e-6) return `${days} day${days > 1 ? "s" : ""}`;
  const remStr = Number.isInteger(rem) ? `${rem}` : `${parseFloat(rem.toFixed(1))}`;
  return `${days} day${days > 1 ? "s" : ""} ${remStr}h`;
};

// tiny UI bits (inline so it works immediately)
const chipStyle = (highlight) => ({
  display: "inline-block",
  padding: "2px 8px",
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 700,
  lineHeight: 1.6,
  background: highlight ? "#fff7ed" : "#eef2ff",
  color: highlight ? "#9a3412" : "#3730a3",
});
const cellStack = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: 6,
};

/* ================= component ================= */

const OwnVehReport = () => {
  useAuthRedirect();
  const { id, branch, basis } = useParams();
  const [data, setData] = useState([]);
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const startYear = 2007;
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const canView = hasPermission("Vehicle Position Report", "view");

 const [site, setSite] = useState({
  ok: true,
  name: "Infraconsoft",
  logo: null,
  address: "",
  email: "",
  phone: "",
  _ts: 0,
});

useEffect(() => {
  let ignore = false;
  (async () => {
    try {
      const res = await axios.get("https://rfs2.infraconsoft.net.in/api/siteinfo");
      const s = res?.data || {};

      // ensure absolute URL if relative
      let logo = s.logo || null;
      if (logo && !/^https?:\/\//i.test(logo)) {
        if (!logo.startsWith("/")) logo = `/${logo}`;
        logo = `${window.location.origin}${logo}`;
      }

      if (!ignore) {
        setSite({
          ok: !!s.ok,
          name: s.name || "Infraconsoft",
          logo,
          address: s.address || "",
          email: s.email || "",
          phone: s.phone || "",
          _ts: Date.now(),
        });
      }
    } catch (e) {
      console.error("Failed to load /siteinfo", e);
    }
  })();
  return () => {
    ignore = true;
  };
}, []);


  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, selectedMonth]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get("https://rfs2.infraconsoft.net.in/api/get_own_veh_report_api", {
        params: { month: selectedMonth, year: selectedYear, branch, id },
      });
      setData(response.data.postionData || []);
      setFilteredData(response.data.postionData || []);
    } catch (error) {
      console.error("Error fetching postionData:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const term = (e.target.value || "").toLowerCase();
    setSearchTerm(term);
    if (!term) {
      setFilteredData(data);
      return;
    }
    const filtered = data.filter((item) =>
      String(item.stn_purnumber || "").toLowerCase().includes(term) ||
      String(item.stn_dpt || "").toLowerCase().includes(term)
    );
    setFilteredData(filtered);
  };

  // Pagination
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentData = filteredData.slice(indexOfFirstItem, indexOfLastItem);

const exportToExcel = () => {
  let cumulative = 0;
  let totalKm = 0;

  const isHour = basis === "Hour";
  const dataHeader = isHour
    ? ["S.No", "Date", "Starting Time", "Closing Time", "Working Hours", "Cumulative Hours", "Number Of Loads", "Location"]
    : ["S.No", "Date", "Starting KM", "Closing KM", "Working KM", "Cumulative KM", "Number Of Loads", "Location"];

  const bodyRows = [];

  currentData.forEach((item, idx) => {
    cumulative += parseFloat(item.fld_wohrs || 0);
    totalKm += parseFloat(item.fld_wokm || 0);

    const dateStr = formatDMY(item.fld_vdate);
    const startAMPM = decToAMPM(item.fld_sthrs);
    const endAMPM = decToAMPM(item.fld_endhrs);
    const workingPretty = formatWorkingTotal(item.fld_wohrs);

    if (isHour) {
      bodyRows.push([
        indexOfFirstItem + idx + 1,
        dateStr,
        startAMPM || "—",
        endAMPM || "—",
        workingPretty || "—",
        cumulative.toFixed(2),
        item.fld_noload ?? "",
        item.fld_location ?? "",
      ]);
    } else {
      bodyRows.push([
        indexOfFirstItem + idx + 1,
        dateStr,
        item.fld_stkm ?? "",
        item.fld_endkm ?? "",
        item.fld_wokm ?? "",
        totalKm.toFixed(2),
        item.fld_noload ?? "",
        item.fld_location ?? "",
      ]);
    }
  });

  // ---------- Header section (site/company info) ----------
  const COLS = dataHeader.length;
  const monthLabel = String(selectedMonth).padStart(2, "0");
  const title = `Own Vehicle Position — ${monthLabel}/${selectedYear}`;

  const fullWidth = (text = "") => [text, ...Array(COLS - 1).fill("")];

  const headerRows = [
    fullWidth((site.name || "Infraconsoft").toUpperCase()),
    fullWidth(site.address || ""),
    fullWidth(
      [site.phone ? `Ph: ${site.phone}` : "", site.email ? `Email: ${site.email}` : ""]
        .filter(Boolean)
        .join(" | ")
    ),
    [],
    fullWidth(title),
    [],
    dataHeader,
  ];

  const aoa = [...headerRows, ...bodyRows];

  const ws = XLSX.utils.aoa_to_sheet(aoa);

  ws["!merges"] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: COLS - 1 } }, // name
    { s: { r: 1, c: 0 }, e: { r: 1, c: COLS - 1 } }, // address
    { s: { r: 2, c: 0 }, e: { r: 2, c: COLS - 1 } }, // phone/email
    { s: { r: 4, c: 0 }, e: { r: 4, c: COLS - 1 } }, // title
  ];

  ws["!cols"] = [
    { wch: 6 }, { wch: 12 }, { wch: 14 }, { wch: 14 },
    { wch: 16 }, { wch: 16 }, { wch: 16 }, { wch: 20 }, { wch: 24 }
  ];

  // Freeze after the header (row index 6 => 7th row)
  ws["!freeze"] = { xSplit: 0, ySplit: 6, topLeftCell: "A7", activePane: "bottomLeft" };

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Vehicle Details");

  const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const blob = new Blob([excelBuffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8"
  });
  saveAs(blob, `vehicle_details_${selectedMonth}-${selectedYear}.xlsx`);
};



  if (!canView) {
    return (
      <div id="admin-container">
        <AdminNav />
        <div className="content-right" style={{ padding: 20 }}>
          <Access />
        </div>
      </div>
    );
  }

  return (
    <div id="admin-container">
      <AdminNav />
      <div className="content-right">
        <section>
          <div className="back-div">
            <h2 className="add-txt">Own Vehicle Position</h2>
            <Link to={`/vehicle_position_report`}>
              <p className="back-button"><i className="fa-solid fa-backward"></i> Back</p>
            </Link>
          </div>
          <Time />
        </section>

        <div className="add-new-container">
          <button className="add-new-btn" onClick={exportToExcel}>
            <i className="fa-solid fa-file-excel"></i> Export to Excel
          </button>
        </div>

        {/* Month & Year Selection */}
        <div className="month-year-container">
          <p className="title">Select your Month & Year:</p>

          <div className="select-container">
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              className="custom-select"
            >
              {[...Array(12).keys()].map((i) => (
                <option key={i + 1} value={i + 1}>
                  {(i + 1).toString().padStart(2, "0")}
                </option>
              ))}
            </select>

            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className="custom-select"
            >
              {[...Array(currentYear - startYear + 1).keys()].map((i) => (
                <option key={startYear + i} value={startYear + i}>
                  {startYear + i}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="content-table">
          <table>
            <thead>
              <tr>
                <th>S.No</th>
                <th>Date</th>

                {basis === "Hour" ? (
                  <>
                    <th>Starting Hours</th>
                    <th>Closing Hours</th>
                    <th>Working Hours</th>
                    <th>Cumulative Hours</th>
                  </>
                ) : (
                  <>
                    <th>Starting KM</th>
                    <th>Closing KM</th>
                    <th>Working KM</th>
                    <th>Cumulative KM</th>
                  </>
                )}

                <th>Number Of Loads</th>
                <th>Location</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="12" className="loading-text">Loading data, please wait...</td>
                </tr>
              ) : currentData.length > 0 ? (
                (() => {
                  let cumulative = 0;
                  let totalKm = 0;

                  return currentData.map((item, index) => {
                    // running totals (unchanged logic)
                    cumulative += parseFloat(item.fld_wohrs || 0);
                    const currentKm = parseFloat(item.fld_wokm || 0);
                    totalKm += currentKm;

                    const dateStr = formatDMY(item.fld_vdate);

                    // AM/PM + formatted totals (Hour basis)
                    const startAMPM = decToAMPM(item.fld_sthrs);
                    const endAMPM = decToAMPM(item.fld_endhrs);
                    const workingPretty = formatWorkingTotal(item.fld_wohrs);

                    // highlight if >= 24 hours
                    const isMultiDay = Number(item.fld_wohrs || 0) >= 24;

                    return (
                      <tr key={index}>
                        <td>{indexOfFirstItem + index + 1}</td>
                        <td>{new Date(item.fld_vdate).toLocaleDateString()}</td>

                        {basis === "Hour" ? (
                          <>
                            {/* Start (aligned & styled) */}
                            <td style={{ verticalAlign: "top" }}>
                              <div style={cellStack}>
                                <span style={chipStyle(isMultiDay)}>{dateStr}</span>
                                <span style={{ fontWeight: 700 }}>{startAMPM || "—"}</span>
                              </div>
                            </td>

                            {/* End (aligned & styled) */}
                            <td style={{ verticalAlign: "top" }}>
                              <div style={cellStack}>
                                <span style={chipStyle(isMultiDay)}>{dateStr}</span>
                                <span style={{ fontWeight: 700 }}>{endAMPM || "—"}</span>
                              </div>
                            </td>

                            {/* Working (formatted), cumulative kept numeric */}
                            <td>{workingPretty || "—"}</td>
                            <td>{cumulative.toFixed(2)}</td>
                          </>
                        ) : (
                          <>
                            <td>{item.fld_stkm}</td>
                            <td>{item.fld_endkm}</td>
                            <td>{item.fld_wokm}</td>
                            <td>{totalKm.toFixed(2)}</td>
                          </>
                        )}

                        <td>{item.fld_noload}</td>
                        <td>{item.fld_location}</td>
                      </tr>
                    );
                  });
                })()
              ) : (
                <tr>
                  <td colSpan="12">No Data Available</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="pagination">
          <button onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1}>
            Previous
          </button>

          <button onClick={() => paginate(1)} className={currentPage === 1 ? "active" : ""}>
            1
          </button>

          {filteredData.length > 0 &&
            [...Array(Math.min(3, Math.ceil(filteredData.length / itemsPerPage) - 1))].map((_, index) => {
              const pageNumber = currentPage + index;
              if (pageNumber > 1 && pageNumber <= Math.ceil(filteredData.length / itemsPerPage)) {
                return (
                  <button
                    key={pageNumber}
                    onClick={() => paginate(pageNumber)}
                    className={currentPage === pageNumber ? "active" : ""}
                  >
                    {pageNumber}
                  </button>
                );
              }
              return null;
            })}

          <button
            onClick={() => paginate(currentPage + 1)}
            disabled={currentPage === Math.ceil(filteredData.length / itemsPerPage)}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default OwnVehReport;
